import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, Send, Package, ShieldCheck } from "lucide-react";
import { formatPrice } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  initiated: "bg-stone-100 text-stone-500",
  completed: "bg-[#2E6B4F]/10 text-[#2E6B4F]",
  shipped: "bg-rose-gold/20 text-terracotta",
  expired: "bg-red-50 text-red-500",
};
const STATUS_LABEL = { initiated: "En attente", completed: "Payée", shipped: "Expédiée", expired: "Expirée" };

const AdminPage = () => {
  const [key, setKey] = useState(sessionStorage.getItem("relax_admin_key") || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shipData, setShipData] = useState({});

  const load = useCallback(async (k) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: { "x-admin-key": k } });
      setOrders(res.data.orders);
      setAuthed(true);
      sessionStorage.setItem("relax_admin_key", k);
    } catch {
      setAuthed(false);
      sessionStorage.removeItem("relax_admin_key");
      if (k) toast.error("Clé administrateur invalide");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (e) => {
    e.preventDefault();
    setKey(input);
    load(input);
  };

  const setShip = (orderId, field, value) =>
    setShipData((prev) => ({ ...prev, [orderId]: { ...prev[orderId], [field]: value } }));

  const ship = async (orderId) => {
    const d = shipData[orderId] || {};
    if (!d.tracking || d.tracking.trim().length < 4) {
      toast.error("Saisissez un numéro de suivi valide");
      return;
    }
    try {
      await axios.post(
        `${API}/orders/${orderId}/ship`,
        { tracking_number: d.tracking.trim(), carrier: d.carrier || "colissimo" },
        { headers: { "x-admin-key": key } }
      );
      toast.success(`Commande ${orderId} expédiée — email de suivi envoyé ✦`);
      load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'expédition");
    }
  };

  if (!authed) {
    return (
      <div data-testid="admin-login" className="pt-40 pb-24 min-h-screen bg-cream flex items-start justify-center px-5">
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-3xl border border-blush-deep p-8 text-center shadow-lg shadow-rose-gold/10">
          <div className="w-14 h-14 mx-auto rounded-full bg-blush flex items-center justify-center mb-5">
            <Lock size={22} className="text-terracotta" />
          </div>
          <h1 className="font-serif text-3xl text-onyx">Espace Admin</h1>
          <p className="text-xs text-muted-warm mt-2 mb-6">Réservé à la gestion des commandes Relax.</p>
          <input
            data-testid="admin-key-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Clé administrateur"
            className="input-elegant text-center mb-4"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-onyx text-cream py-3.5 text-sm font-semibold hover:bg-terracotta transition-colors disabled:opacity-60"
          >
            {loading ? "Vérification…" : "Accéder aux commandes"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="pt-32 pb-24 min-h-screen bg-cream">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-onyx">Commandes</h1>
            <p className="text-xs text-muted-warm mt-1 flex items-center gap-1.5">
              <ShieldCheck size={13} className="text-[#2E6B4F]" /> {orders.length} commande{orders.length > 1 ? "s" : ""} · l'expédition envoie automatiquement l'email de suivi
            </p>
          </div>
          <button
            data-testid="admin-refresh-button"
            onClick={() => load(key)}
            className="rounded-full border border-blush-deep bg-white px-4 py-2.5 text-xs font-medium text-onyx hover:border-rose-gold transition-colors flex items-center gap-2"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualiser
          </button>
        </div>

        {orders.length === 0 && !loading && (
          <p data-testid="admin-empty" className="text-center text-muted-warm text-sm py-20">Aucune commande pour le moment.</p>
        )}

        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="bg-white rounded-3xl border border-blush-deep p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-blush flex items-center justify-center"><Package size={17} className="text-terracotta" /></span>
                  <div>
                    <p className="font-mono text-sm font-bold text-onyx">{o.order_id}</p>
                    <p className="text-[11px] text-muted-warm">{new Date(o.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {o.mock && <span className="text-[10px] font-mono uppercase bg-stone-100 text-stone-500 rounded-full px-3 py-1">Démo</span>}
                  <span data-testid={`admin-order-${o.order_id}-status`} className={`text-[10px] font-mono uppercase rounded-full px-3 py-1 ${STATUS_STYLE[o.status] || STATUS_STYLE.initiated}`}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-warm mb-2">Cliente</p>
                  <p className="text-onyx font-medium">{o.customer.first_name} {o.customer.last_name}</p>
                  <p className="text-muted-warm text-xs mt-0.5">{o.customer.email} · {o.customer.phone}</p>
                  <p className="text-muted-warm text-xs mt-0.5">{o.customer.address}, {o.customer.zip} {o.customer.city}</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-warm mb-2">Contenu</p>
                  {o.items.map((it) => (
                    <p key={it.lookup_key} className="text-xs text-onyx">{it.name} × {it.quantity}</p>
                  ))}
                  <p className="text-sm font-semibold text-terracotta mt-2">
                    {formatPrice(o.total_cents / 100)} · {o.payment_method === "paypal" ? "PayPal" : "CB"}
                    {o.discount_cents > 0 && <span className="text-[#2E6B4F] font-normal text-xs"> · code {o.promo_code}</span>}
                  </p>
                </div>
              </div>
              {o.status === "shipped" ? (
                <div data-testid={`admin-order-${o.order_id}-tracking`} className="mt-4 rounded-2xl bg-[#2E6B4F]/5 border border-[#2E6B4F]/20 px-4 py-3 text-xs text-[#2E6B4F] flex items-center gap-2">
                  <Send size={13} /> Expédiée via {o.carrier === "chronopost" ? "Chronopost" : "Colissimo"} · Suivi <span className="font-mono font-bold">{o.tracking_number}</span>
                </div>
              ) : o.payment_status === "paid" ? (
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <input
                    data-testid={`admin-order-${o.order_id}-tracking-input`}
                    type="text"
                    placeholder="Numéro de suivi (ex. 8V12345678901)"
                    value={(shipData[o.order_id] || {}).tracking || ""}
                    onChange={(e) => setShip(o.order_id, "tracking", e.target.value)}
                    className="input-elegant flex-1 !py-2.5 text-xs font-mono"
                  />
                  <select
                    data-testid={`admin-order-${o.order_id}-carrier-select`}
                    value={(shipData[o.order_id] || {}).carrier || "colissimo"}
                    onChange={(e) => setShip(o.order_id, "carrier", e.target.value)}
                    className="input-elegant !py-2.5 text-xs sm:w-40"
                  >
                    <option value="colissimo">Colissimo</option>
                    <option value="chronopost">Chronopost</option>
                  </select>
                  <button
                    data-testid={`admin-order-${o.order_id}-ship-button`}
                    onClick={() => ship(o.order_id)}
                    className="rounded-xl bg-terracotta text-cream px-5 py-2.5 text-xs font-semibold hover:bg-onyx transition-colors flex items-center justify-center gap-2"
                  >
                    <Send size={13} /> Expédier + email
                  </button>
                </div>
              ) : (
                <p className="mt-4 text-xs text-muted-warm">En attente du paiement — l'expédition sera possible une fois la commande payée.</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
